import {
  Component,
  ViewEncapsulation,
  ViewChild,
  OnInit
} from '@angular/core';
import {
  AbstractBlStaticGuide,
  ModalReason,
  ModalButtonSetting,
  ModalButtonColor,
  BlModalContentComponent,
  ModalOption,
  ModalButtonType,
  BlModalService,
  Era,
  DateMode,
  BlDialogService,
  BlSelectComponent,
  InputGroupType
} from '@blcloud/bl-ng-ui-component';
import {
  BalanceType,
} from './debt-export-condition-modal.define';
import { DateEra, DateSeparator, DateTimeOutput, DateTimePadding, DateTimeUtils } from '@blcloud/bl-common';
import {
  toNumber as _toNumber,
  defer as _defer
} from 'lodash';
import {
  OrganizationGuideComponent,
  OrganizationInformationResourceService,
  SupplierInfoResourceService
} from '@blcloud/bl-ng-share-module';
import { SupplierGuideComponent } from '@blcloud/bl-ng-vehicle-customer-module';
import { IAccpayBalanceExtractCondition, IOrganizationInformation, ISupplierInfo } from '@blcloud/bl-datamodel';
import { PartialObserver } from 'rxjs/Observer';
import { Observable } from 'rxjs/Observable';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { AccPayBalanceDataOutputType } from '@blcloud/bl-datamodel/enum/bizcmn/acc-pay-balance-data-output-type';

@Component({
  templateUrl: './debt-export-condition-modal.component.html',
  styleUrls: ['./debt-export-condition-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DebtExportConditionModalComponent extends AbstractBlStaticGuide implements OnInit {
  @ViewChild('content') blModalContentComponent: BlModalContentComponent;

  // 残高種別
  @ViewChild('balanceType') balanceType: BlSelectComponent;

  readonly Era = Era;

  readonly DateMode = DateMode;

  /** ラジオボタングループタイプ */
  readonly RADIO_GROUP_TYPE: InputGroupType = InputGroupType.Inline;

  /** データ出力タイプ区分リスト */
  readonly DATA_EXPORT_DIV_LIST = [
    { code: AccPayBalanceDataOutputType.Payee, name: '支払先別' },
    { code: AccPayBalanceDataOutputType.SupplierOrg, name: '仕入先組織別' }
  ];

  /** ガイドフッターのボタン配置 */
  footerButtonType = ModalButtonType.OkCancel;
  okButtonSetting: ModalButtonSetting = {
    color: ModalButtonColor.Success,
    label: '決定',
  };

  /** 検索条件 */
  condition: IAccpayBalanceExtractCondition = {};

  /** 残高種別 */
  balanceTypeItems = [
    { code: BalanceType.PAY, name: '支払' },
    { code: BalanceType.ACCPAY, name: '買掛' }
  ];

  /** 組織リソース */
  organizationResource: {};

  /** 仕入先リソース */
  supplierResource: {};

  constructor(
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    public organizationResourceService: OrganizationInformationResourceService,
    public supplierInfoResourceService: SupplierInfoResourceService,
  ) {
    super();
  }

  /**
   * 初期化処理
   */
  ngOnInit(): void {
    this.organizationResource = this.getOrganizationInnerResource();
    this.supplierResource = this.getSupplierInnerResource();
  }

  /**
   * @override
   */
  getResults() {
    return {condition: this.condition};
  }

  /**
   * @override
   */
  defaultOption(): ModalOption {
    return { class: 'modelsize-middle' };
  }

  /**
   * 画面表示後にデータローディング処理
   */
  onShown() {
    this.setConditionData();
    // 初期フォーカス設定
    _defer(() => this.balanceType.focus());
  }

  /**
   * モーダルウィンドウを終了する操作が行われた際にコールされます。
   * @param reason 終了理由
   */
  dismiss(reason: ModalReason) {
    switch (reason) {
      case ModalReason.Done:
        if (this.validateCondition()) {
          this.emitDismissEvent(reason);
        }
        break;
      default:
        this.emitDismissEvent(reason);
        break;
    }
  }

  onShowGuide(id: string) {
    switch (id) {
      case 'organizationCodeStart':
        this.showOrganizationGuide('organizationCodeStart');
        break;
      case 'organizationCodeEnd':
        this.showOrganizationGuide('organizationCodeEnd');
        break;
      case 'supplierCdStart':
        this.showSupplierGuide('supplierCdStart');
        break;
      case 'supplierCdEnd':
        this.showSupplierGuide('supplierCdEnd');
        break;
      default:
        break;
    }
  }

  /**
   * 組織ガイドを表示する
   * @param propertyId 項目ID
   */
  private showOrganizationGuide(propertyId: string): void {
    this.modalService.show(OrganizationGuideComponent, {  }).subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          if (propertyId === 'organizationCodeStart') {
            this.condition.organizationCodeStart = modalRef.getResults() ? modalRef.getResults().organizationCode : '';
          } else {
            this.condition.organizationCodeEnd = modalRef.getResults() ? modalRef.getResults().organizationCode : '';
          }
        }
        modalRef.hide();
      }
    );
  }

  /**
   * 得意先ガイドを表示する
   * @param propertyId 項目ID
   */
  private showSupplierGuide(propertyId: string): void {
    this.modalService.show(SupplierGuideComponent, {  }).subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          if (propertyId === 'supplierCdStart') {
            this.condition.supplierCdStart = modalRef.getResults() ? modalRef.getResults().supplierCd : '';
          } else {
            this.condition.supplierCdEnd = modalRef.getResults() ? modalRef.getResults().supplierCd : '';
          }
        }
        modalRef.hide();
      }
    );
  }

  /** チェックボックス選択イベント */
  onChangeCheckboxEvent(event: any) {
    this.condition.actualNoneOutputFlag = event.currentTarget.checked;
  }

  /**
   * ヘッダーデータ設定
   */
  private setConditionData() {
    const systemMonth = _toNumber(DateTimeUtils.format(
      DateTimeUtils.today(), DateTimeOutput.YM, DateTimePadding.ZERO, DateSeparator.NONE, DateEra.AD));
    this.condition = {
      organizationCodeStart: undefined,
      organizationCodeEnd: undefined,
      supplierCdStart: undefined,
      supplierCdEnd: undefined,
      targetYearMonthStart: systemMonth,
      targetYearMonthEnd: systemMonth,
      balanceType: BalanceType.PAY,
      actualNoneOutputFlag: true,
      accPayBalanceDataOutputType: AccPayBalanceDataOutputType.Payee,
    };
  }

  /**
   * 検索条件チェック
   */
  validateCondition(): boolean {
    // 拠点（Ｆｒｏｍ）
    const organizationCodeStart = this.condition.organizationCodeStart;
    // 拠点（Ｔｏ）
    const organizationCodeEnd = this.condition.organizationCodeEnd;
    // 仕入先（Ｆｒｏｍ）
    const supplierCdStart = this.condition.supplierCdStart;
    // 仕入先（Ｔｏ）
    const supplierCdEnd = this.condition.supplierCdEnd;
    // 対象年月（Ｆｒｏｍ）
    const targetYearMonthStart = this.condition.targetYearMonthStart === 190001 ?
      null : this.condition.targetYearMonthStart;
    // 対象年月（Ｔｏ）
    const targetYearMonthEnd = this.condition.targetYearMonthEnd === 190001 ?
      null : this.condition.targetYearMonthEnd;

    if (organizationCodeStart && organizationCodeEnd && organizationCodeStart > organizationCodeEnd) {
      const sub = this.dialogService.warn('拠点の範囲指定に誤りがあります。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (supplierCdStart && supplierCdEnd && supplierCdStart > supplierCdEnd) {
      const sub = this.dialogService.warn('仕入先の範囲指定に誤りがあります。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (!targetYearMonthStart) {
      const sub = this.dialogService.warn('対象年月（Ｆｒｏｍ）を入力してください。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (!targetYearMonthEnd) {
      const sub = this.dialogService.warn('対象年月（Ｔｏ）を入力してください').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (targetYearMonthStart > targetYearMonthEnd) {
      const sub = this.dialogService.warn('開始日を終了日よりも後にすることはできません。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }

    return true;
  }

  /**
   * 組織ガイド用のリソースを取得する
   */
  private getOrganizationInnerResource(): {} {
    return {
      getById: (code: string) => {
        return Observable.create(
          (observer: PartialObserver<IOrganizationInformation>) => {
            // まず、初期取得するデータから検索
            this.organizationResourceService.getByIdUseCache(code).subscribe(
              result => {
                if (!result || result.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
                  observer.error('該当データが存在しません。');
                } else {
                  observer.next(result);
                  observer.complete();
                }
              }
            );
          }
        );
      }
    };
  }

  /**
   * 仕入先ガイド用のリソースを取得する
   */
  private getSupplierInnerResource(): {} {
    return {
      getById: (code: string) => {
        return Observable.create(
          (observer: PartialObserver<ISupplierInfo>) => {
            // まず、初期取得するデータから検索
            this.supplierInfoResourceService.getByIdUseCache(code).subscribe(
              result => {
                if (!result || result.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
                  observer.error('該当データが存在しません。');
                } else {
                  observer.next(result);
                  observer.complete();
                }
              }
            );
          }
        );
      }
    };
  }

  /**
   * データ出力タイプ変更イベントハンドラ
   * @param accPayBalanceDataOutputType データ出力区分
   */
  onChangeDataExportDiv(accPayBalanceDataOutputType: string) {
    this.condition.accPayBalanceDataOutputType = accPayBalanceDataOutputType;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/export-modal/debt-export-condition-modal/debt-export-condition-modal.component.ts